import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import settingsAPI from '../../../api/settings';

export function useAuthPage() {
  const navigate = useNavigate();
  const { login, signup, guestLogin, requestOTP } = useAuth();
  const [loading, setLoading] = useState(false);

  // Guest login visibility — defaults to HIDDEN until we know the real
  // value, to avoid a "flash then hide" flicker for accounts where the
  // admin has disabled it. We seed from a same-session cache (closest we
  // can get to SSR in a CRA SPA) so repeat visits within the session never
  // flicker either; a brand-new session just stays hidden a beat longer
  // instead of flashing visible.
  const cachedGuestEnabled = (() => {
    try {
      const cached = sessionStorage.getItem('guestLoginEnabledCache');
      return cached === null ? null : cached === 'true';
    } catch {
      return null;
    }
  })();
  const [guestLoginEnabled, setGuestLoginEnabled] = useState(cachedGuestEnabled ?? false);
  const [settingsLoaded, setSettingsLoaded] = useState(cachedGuestEnabled !== null);

  // Check if there's a booking intent to default to guest tab
  const bookingIntent = localStorage.getItem('bookingIntent');
  const defaultToGuest = bookingIntent ? JSON.parse(bookingIntent)?.defaultToGuest : false;
  const [activeTab, setActiveTab] = useState(defaultToGuest && guestLoginEnabled ? 'guest' : 'login');

  // Fetch public settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await settingsAPI.getPublicSettings();
      const enabled = settings.guest_login_enabled ?? false;
      setGuestLoginEnabled(enabled);
      setSettingsLoaded(true);
      try {
        sessionStorage.setItem('guestLoginEnabledCache', String(enabled));
      } catch { /* sessionStorage unavailable — non-fatal */ }

      // If guest login is disabled and user was on guest tab, switch to login
      if (!enabled && activeTab === 'guest') {
        setActiveTab('login');
      }
    };
    fetchSettings();
    // Deliberate mount-once effect — `activeTab` is read at fetch-completion
    // time (whatever tab the user is on then), not a trigger to re-fetch
    // settings every time the user switches tabs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login state
  // Pre-fill email from localStorage when the user has previously opted in.
  // We store the preference itself ('lastEmailRemember') alongside the value so
  // that an opt-out persists across logins and we don't re-save the email
  // silently next time. Defaults to "remember" for first-time visitors.
  const [loginEmail, setLoginEmail] = useState(() => {
    try {
      const remember = localStorage.getItem('lastEmailRemember');
      if (remember === 'false') return '';
      return localStorage.getItem('lastEmail') || '';
    } catch {
      return '';
    }
  });
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(() => {
    try {
      return localStorage.getItem('lastEmailRemember') !== 'false';
    } catch {
      return true;
    }
  });

  // Signup state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupCountryCode, setSignupCountryCode] = useState('+673'); // Default to Brunei
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');

  // Guest state
  const [guestName, setGuestName] = useState('');
  const [guestCountryCode, setGuestCountryCode] = useState('+673'); // Default to Brunei
  const [guestPhone, setGuestPhone] = useState('');
  const [guestOTP, setGuestOTP] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');

      // Persist the "remember email" preference + value (or clear if opted out)
      try {
        if (rememberEmail) {
          localStorage.setItem('lastEmail', loginEmail);
          localStorage.setItem('lastEmailRemember', 'true');
        } else {
          localStorage.removeItem('lastEmail');
          localStorage.setItem('lastEmailRemember', 'false');
        }
      } catch { /* localStorage unavailable — non-fatal */ }

      // Check if there's a booking intent
      const intent = localStorage.getItem('bookingIntent');
      if (intent) {
        navigate('/book');
      } else {
        navigate('/home');
      }
    } catch (error) {
      // Soft-landing for deactivated accounts — instead of a raw toast
      // that looks transient, route to a dedicated explainer page with
      // a Contact Support CTA. The backend returns 403 with a detail
      // string containing "deactivated" (see auth_service.login_user).
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || '';
      if (status === 403 && /deactivated/i.test(detail)) {
        navigate('/account-deactivated');
        return;
      }
      toast.error(detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSavedEmail = () => {
    try {
      localStorage.removeItem('lastEmail');
      localStorage.setItem('lastEmailRemember', 'false');
    } catch { /* non-fatal */ }
    setLoginEmail('');
    setRememberEmail(false);
    toast.success('Saved email cleared from this device');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Concatenate country code with local phone number
      const fullPhone = signupCountryCode + signupPhone;
      const result = await signup(signupName, signupEmail, fullPhone, signupPassword);

      // New flow: Check if email verification is required
      if (result.requires_verification) {
        toast.success('Verification email sent! Please check your inbox.');
        // Redirect to email verification page
        navigate('/verify-email', {
          state: {
            email: signupEmail,
            name: signupName
          }
        });
      } else {
        // Legacy flow (should not happen with new backend)
        toast.success('Account created successfully!');
        const intent = localStorage.getItem('bookingIntent');
        if (intent) {
          navigate('/book');
        } else {
          navigate('/home');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!guestName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!guestPhone) {
      toast.error('Please enter phone number');
      return;
    }
    setLoading(true);
    try {
      const result = await requestOTP(guestCountryCode, guestPhone);
      setOtpRequested(true);
      toast.success(`OTP sent! Use: ${result.mock_otp}`);
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await guestLogin(guestName, guestCountryCode, guestPhone, guestOTP);
      toast.success('Welcome!');

      // Check if there's a booking intent
      const intent = localStorage.getItem('bookingIntent');

      // Small delay to ensure state updates
      setTimeout(() => {
        if (intent) {
          navigate('/book');
        } else {
          navigate('/home');
        }
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectAfter = window.location.origin + '/auth';
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${apiBase}/api/auth/google/init?redirect_after=${encodeURIComponent(redirectAfter)}`;
  };

  return {
    loading, guestLoginEnabled, settingsLoaded, activeTab, setActiveTab,
    loginEmail, setLoginEmail, loginPassword, setLoginPassword, rememberEmail, setRememberEmail,
    signupName, setSignupName, signupEmail, setSignupEmail,
    signupCountryCode, setSignupCountryCode, signupPhone, setSignupPhone, signupPassword, setSignupPassword,
    guestName, setGuestName, guestCountryCode, setGuestCountryCode, guestPhone, setGuestPhone,
    guestOTP, setGuestOTP, otpRequested, setOtpRequested,
    handleLogin, handleClearSavedEmail, handleSignup, handleRequestOTP, handleGuestLogin, handleGoogleSignIn,
  };
}
